import { isContentOnly } from "utils/appMode";
import { CatalogueDeploymentRow } from "routes/createContent/components/CatalogueDeploymentRow";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ExerciseEditor from "../../components/ExerciseEditor";
import useGetExercise from "../../api/useGetExercise";
import { NavLink, useParams, Link, useHistory } from "react-router-dom";
import { nullToUndefined } from "../../utils/exerciseHelpers";
import { Button, IconButton, TextField, Tooltip, Typography } from "@material-ui/core";
import { ArrowBack, ChevronLeft, ChevronRight, Delete, Visibility } from "@material-ui/icons";
import { CataloguePath, exerciseEditPath } from "routes/createContent/paths";
import { submitUpdateExercise } from "../../utils/submitExercise";
import DeletedExerciseWarning from "../../components/DeletedExerciseWarning";
import { ConfirmDeleteDialog } from "routes/createContent/components/CatalogueList";
import { SuccessErrorSnackbar } from "../../components/SuccessErrorSnackbar";
import { useDeleteExercise } from "../../api/useDeleteExercise";
import { useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";

export const transformToEditor = (data: any): any => {
  // Transform the data to the format expected by the ExerciseEditor
  return {
    ...data,
    levels: data.levels.map((level: any) => ({
      ...level,
      translations: level.translations.map((trans: any) => ({
        ...trans,
        video:
          trans.video_file_url || trans.video_thumbnail_file_url
            ? {
                video_file_url: trans.video_file_url || undefined,
                video_thumbnail_file_url: trans.video_thumbnail_file_url || undefined,
              }
            : undefined,
      })),
    })),
  };
};

export const EditExercise: React.VFC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const exerciseId = parseInt(id, 10);
  const { data: exercise } = useGetExercise(exerciseId);
  const exerciseName = exercise?.name;
  const [inputId, setInputId] = useState("");
  const [showSaveWarning, setShowSaveWarning] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const queryClient = useQueryClient();
  const deleteExercise = useDeleteExercise();

  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (showSaveWarning) {
      const confirmLeave = window.confirm(t("edit_exercise.unsaved_changes_warning"));
      if (!confirmLeave) {
        e.preventDefault();
        return;
      }
    }
  };

  const handleDeleteExercise = () => {
    deleteExercise.mutate(exerciseId, {
      onSuccess: () => {
        setDeleteConfirmationOpen(false);
        history.push(CataloguePath.Exercises);
      },
      onError: error => {
        console.error("Failed to delete exercise:", error);
        setDeleteConfirmationOpen(false);
        alert(t("edit_exercise.delete_error", { error: String(error) }));
      },
    });
  };

  useEffect(() => {
    setShowSaveWarning(false);
  }, [id]);

  if (!id) {
    return (
      <div>
        <p>{t("edit_exercise.enter_id_prompt")}</p>
        <TextField
          label={t("edit_exercise.exercise_id_label")}
          variant="outlined"
          value={inputId}
          onChange={e => setInputId(e.target.value)}
          fullWidth
          style={{ marginBottom: 16 }}
        />

        <Button variant="contained" color="primary" component={NavLink} to={exerciseEditPath(inputId)}>
          {t("edit_exercise.edit_button", { inputId })}
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* @ts-ignore */}
      {exercise?.deleted_at && <DeletedExerciseWarning />}
      {exercise && (
        <ExerciseEditor
          key={exerciseId}
          initialFormData={transformToEditor(nullToUndefined(exercise))}
          onDirty={dirty => setShowSaveWarning(dirty)}
          isSubmitting={isSubmitting}
          submitButtonText="Save"
          onSubmit={async data => {
            setIsSubmitting(true);
            try {
              const id = await submitUpdateExercise(data, nullToUndefined(exercise));
              queryClient.invalidateQueries(queryKeys.getExercises());
              setShowSaveWarning(false);
              setSnackbar({ open: true, message: t("edit_exercise.update_success", { id }), severity: "success" });
            } catch (e) {
              setSnackbar({
                open: true,
                message: t("edit_exercise.update_error", { error: String(e) }),
                severity: "error",
              });
            } finally {
              setIsSubmitting(false);
            }
          }}
          toolbar={
            <>
              <Tooltip title="Back to exercises">
                <IconButton size="small" component={Link} to={CataloguePath.Exercises} onClick={handleNavigation}>
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("edit_exercise.previous")}>
                <span>
                  <IconButton
                    size="small"
                    component={Link}
                    to={exerciseEditPath(exerciseId - 1)}
                    disabled={exerciseId <= 1}
                    onClick={handleNavigation}
                  >
                    <ChevronLeft />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2">{t("edit_exercise.id_label", { exerciseId })}</Typography>
              <Tooltip title={t("edit_exercise.next")}>
                <IconButton
                  size="small"
                  component={Link}
                  to={exerciseEditPath(exerciseId + 1)}
                  onClick={handleNavigation}
                >
                  <ChevronRight />
                </IconButton>
              </Tooltip>
              {isContentOnly && exerciseName && (
                <CatalogueDeploymentRow kind="exercise" name={exerciseName} rollout={exercise?.rollout} />
              )}
            </>
          }
          actions={
            <>
              <Button
                size="small"
                style={{ color: "#f44336" }}
                onClick={() => setDeleteConfirmationOpen(true)}
                startIcon={<Delete />}
              >
                {t("edit_exercise.delete_button")}
              </Button>
              <Button
                size="small"
                component={Link}
                to={`${CataloguePath.Exercises}/${exerciseId}`}
                startIcon={<Visibility />}
                onClick={handleNavigation}
              >
                {t("edit_exercise.view_button")}
              </Button>
            </>
          }
        />
      )}

      <ConfirmDeleteDialog
        noun="exercise"
        open={deleteConfirmationOpen}
        loading={deleteExercise.isLoading}
        onCancel={() => setDeleteConfirmationOpen(false)}
        onConfirm={handleDeleteExercise}
      />

      <SuccessErrorSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};
